import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useAuth } from '../../../../contexts/AuthContext';
import apiService from '../../../../services/api';
import SidePanel from '../../../UI/SidePanel';
import CustomDropdown from '../../../UI/CustomDropdown';
import EmptyState from '../../../UI/EmptyState';
import SkeletonLoader from '../../../UI/SkeletonLoader';
import sidePanelStyles from '../../../../styles/SidePanel.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import customDropdownStyles from '../../../../styles/CustomDropdown.module.css';
import styles from '../../../../styles/TaskManagementTab.module.css';
import skeletonStyles from '../../../../styles/SkeletonLoader.module.css';
import taskFormStyles from '../../../../styles/TaskForm.module.css';
import { Task } from '../types';

export interface TaskInitiationFormData {
  name: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed';
  hours_worked?: number;
  work_done?: string;
  template_id: string;
}

interface TaskInitiationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availableTemplates: Task[];
  isLoadingTemplates: boolean;
  onLoadTemplates: () => void;
  contextGroupId?: string | null;
  enableCustomProposal?: boolean;
}

interface AcademicRow {
  sNo: number;
  courseName: string;
  lectureCompleted: number;
  yearSem: string;
  noStudents: number;
  noDistinctions: number;
  noFirstClasses: number;
  noPassed: number;
}

interface FeedbackRow {
  sNo: number;
  courseName: string;
  yearSem: string;
  noStudents: number;
  feedbackPercentage: number;
}

interface ResearchPaperRow {
  title: string;
  journalName: string;
  issnIsbn: string;
  indexing: 'SCI' | 'Scopus' | 'UGC' | 'Web of Science' | 'Other';
  impactFactor: string;
  coAuthors: number;
  isMainAuthor: boolean; // First/Corresponding/Guide
}

interface TeachingActivityRow {
  activityDescription: string;
  qualityRating: 'Outstanding' | 'Average' | 'Modest';
}

interface ProfessionalDevelopmentRow {
  activityType: 'Extension Activity' | 'Admin Role' | 'FDP' | 'Conference' | 'Other';
  description: string;
  roleOrDuration: 'Leading/2+ Weeks/International' | 'Member/1 Week/National' | 'Participant/Low Duration';
}

interface ResearchProjectRow {
  category: 'Book' | 'Chapter' | 'Project' | 'Consultancy' | 'Guidance';
  title: string;
  details: string; // Agency, Publisher, University
  periodOrYear: string;
  amountOrCount: string; // Grant Amount, ISBN, or Count
  status: 'Ongoing' | 'Completed' | 'Submitted' | 'Published';
  policyOrPatent: boolean; // For Projects
}

const TaskInitiationForm: React.FC<TaskInitiationFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  availableTemplates,
  isLoadingTemplates,
  onLoadTemplates,
  contextGroupId,
  enableCustomProposal = false,
}) => {
  const notificationService = useNotification();
  const { user } = useAuth();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<Task | null>(null);
  const [templateDetails, setTemplateDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<TaskInitiationFormData>({
    name: '',
    description: '',
    status: 'pending',
    hours_worked: undefined,
    work_done: '',
    template_id: '',
  });

  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [documentMetadata, setDocumentMetadata] = useState<Record<string, any>>({});
  const [isValidatingFiles, setIsValidatingFiles] = useState(false);

  // Structured Data States
  const [academicData, setAcademicData] = useState<AcademicRow[]>([
    { sNo: 1, courseName: '', lectureCompleted: 0, yearSem: '', noStudents: 0, noDistinctions: 0, noFirstClasses: 0, noPassed: 0 }
  ]);
  const [feedbackData, setFeedbackData] = useState<FeedbackRow[]>([
    { sNo: 1, courseName: '', yearSem: '', noStudents: 0, feedbackPercentage: 0 }
  ]);
  const [researchData, setResearchData] = useState<ResearchPaperRow[]>([
    { title: '', journalName: '', issnIsbn: '', indexing: 'SCI', impactFactor: '', coAuthors: 0, isMainAuthor: true }
  ]);
  const [teachingData, setTeachingData] = useState<TeachingActivityRow[]>([
    { activityDescription: '', qualityRating: 'Outstanding' }
  ]);
  const [professionalData, setProfessionalData] = useState<ProfessionalDevelopmentRow[]>([
    { activityType: 'Other', description: '', roleOrDuration: 'Leading/2+ Weeks/International' }
  ]);
  const [researchProjectData, setResearchProjectData] = useState<ResearchProjectRow[]>([
    { category: 'Project', title: '', details: '', periodOrYear: '', amountOrCount: '', status: 'Ongoing', policyOrPatent: false }
  ]);

  const [facultyScore, setFacultyScore] = useState<number>(0);
  const [researchScore, setResearchScore] = useState<number>(0);
  const [teachingScore, setTeachingScore] = useState<number>(0);
  const [professionalScore, setProfessionalScore] = useState<number>(0);
  const [researchProjectScore, setResearchProjectScore] = useState<number>(0);

  // Academic Year State
  const [academicYear, setAcademicYear] = useState<string>('');
  const [academicYearOptions, setAcademicYearOptions] = useState<{ value: string, label: string }[]>([]);

  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        const response = await apiService.getAcademicYears();
        // Handle both wrapped {status: 'success', data: [...]} and raw array responses
        const data = response?.data || (Array.isArray(response) ? response : []);

        if (Array.isArray(data)) {
          const options = data.map((year: any) => ({
            value: year.id.toString(), // Use ID for value
            label: year.name
          }));
          setAcademicYearOptions(options);

          // Set default to current year
          const currentYear = data.find((y: any) => y.is_current);
          if (currentYear) {
            setAcademicYear(currentYear.id.toString());
          } else if (options.length > 0) {
            setAcademicYear(options[0].value);
          }
        }
      } catch (error: any) {
        console.error("Failed to fetch academic years:", error);
        notificationService.error(error.response?.data?.message || error.response?.data?.error || "Failed to load academic years");
      }
    };
    fetchAcademicYears();
  }, []);

  // Template Detection
  const isAcademicPerformance = selectedTemplate?.name.toLowerCase().includes('academic performance');
  const isFeedback = selectedTemplate?.name.toLowerCase().includes('feedback');
  const isResearchPublication = selectedTemplate?.name.toLowerCase().includes('research publication') || selectedTemplate?.name.toLowerCase().includes('research paper');
  const isTeachingActivity = selectedTemplate?.name.toLowerCase().includes('teaching') || selectedTemplate?.name.toLowerCase().includes('innovative teaching');
  const isResearchProject = selectedTemplate?.name.toLowerCase().includes('research project') || selectedTemplate?.name.toLowerCase().includes('guidance');
  const isProfessionalDevelopment = selectedTemplate?.name.toLowerCase().includes('professional development') || selectedTemplate?.name.toLowerCase().includes('self appraisal - part b');

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ];

  useEffect(() => {
    if (selectedTemplateId && selectedTemplateId !== 'others') {
      const loadDetails = async () => {
        setLoadingDetails(true);
        try {
          const response = await apiService.getTaskDetails(selectedTemplateId);
          if (response.status === 'success') {
            setTemplateDetails(response.data || response);
            const template = response.data || response;
            setFormData(prev => ({
              ...prev,
              name: template.name || '',
              description: template.description || '',
              status: 'pending',
              work_done: '',
              hours_worked: undefined,
              template_id: template.id
            }));
            if (!selectedTemplate) setSelectedTemplate(template);
          }
        } catch (error: any) {
          console.error('Error loading template details:', error);
          notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load template details');
        } finally {
          setLoadingDetails(false);
        }
      };
      loadDetails();
    } else if (selectedTemplateId === 'others') {
      setTemplateDetails(null);
      setLoadingDetails(false);
    }
  }, [selectedTemplateId]);

  useEffect(() => {
    loadUserGroups();
  }, [user?.id]);

  useEffect(() => {
    if (contextGroupId) {
      setSelectedGroupId(contextGroupId);
    }
  }, [contextGroupId]);

  useEffect(() => {
    if (isAcademicPerformance) {
      calculateFacultyScore();
    }
  }, [academicData, isAcademicPerformance]);

  useEffect(() => {
    if (isResearchPublication) {
      calculateResearchScore();
    }
  }, [researchData, isResearchPublication]);

  useEffect(() => {
    if (isTeachingActivity) {
      calculateTeachingScore();
    }
  }, [teachingData, isTeachingActivity]);

  useEffect(() => {
    if (isResearchProject) {
      calculateResearchProjectScore();
    }
  }, [researchProjectData, isResearchProject]);

  const loadUserGroups = async () => {
    try {
      const response = await apiService.getUserGroups();
      if (response.status === 'success') {
        const groups = response.data || [];
        setUserGroups(groups);

        // Auto-select if only one group
        if (groups.length === 1 && !selectedGroupId) {
          setSelectedGroupId(groups[0].id);
        }
      }
    } catch (error: any) {
      console.error('Error loading groups:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load groups');
    }
  };

  const calculateFacultyScore = () => {
    if (academicData.length === 0) {
      setFacultyScore(0);
      return;
    }

    let totalScore = 0;
    academicData.forEach(row => {
      // Formula: (Pass% * 30 + FirstClass% * 5 + Distinction% * 15) / 100
      const students = row.noStudents || 1; // Prevent division by zero

      const distinctionPercentage = (row.noDistinctions / students) * 100;
      const firstClassPercentage = (row.noFirstClasses / students) * 100;
      const passPercentage = (row.noPassed / students) * 100;

      const distinctionScore = (distinctionPercentage / 100) * 15;
      const firstClassScore = (firstClassPercentage / 100) * 5;
      const passScore = (passPercentage / 100) * 30;

      totalScore += (distinctionScore + firstClassScore + passScore);
    });

    // Calculate Average
    const averageScore = totalScore / academicData.length;
    setFacultyScore(parseFloat(averageScore.toFixed(2)));
  };

  const calculateResearchScore = () => {
    let totalScore = 0;
    researchData.forEach(row => {
      let baseScore = 0;
      // Base score logic
      if (row.indexing === 'SCI') baseScore = 20;
      else if (row.indexing === 'Scopus' || row.indexing === 'Web of Science') baseScore = 15;
      else if (row.indexing === 'UGC') baseScore = 10;

      // Authorship logic
      let finalScore = 0;
      if (row.isMainAuthor) {
        finalScore = baseScore;
      } else {
        // Co-author logic
        // If total authors <= 4 (including self, so coAuthors + 1 <= 4), score = base/2
        // IF total authors > 4, score = base/3
        const totalAuthors = row.coAuthors + 1; // self + co-authors
        if (totalAuthors <= 4) {
          finalScore = baseScore / 2;
        } else {
          finalScore = baseScore / 3;
        }
      }
      totalScore += finalScore;
    });
    setResearchScore(parseFloat(totalScore.toFixed(2)));
  };

  const calculateTeachingScore = () => {
    let totalScore = 0;
    teachingData.forEach(row => {
      let score = 0;
      if (row.qualityRating === 'Outstanding') score = 5;
      else if (row.qualityRating === 'Average') score = 3;
      else if (row.qualityRating === 'Modest') score = 1;
      totalScore += score;
    });
    setTeachingScore(totalScore);
  };

  const handleTemplateSelect = async (templateId: string | string[]) => {
    const id = Array.isArray(templateId) ? templateId[0] : templateId;
    if (!id) {
      setSelectedTemplateId('');
      setSelectedTemplate(null);
      setTemplateDetails(null);
      return;
    }

    setSelectedTemplateId(id);

    if (id === 'others') {
      setSelectedTemplate(null);
      setTemplateDetails(null);
      setFormData(prev => ({
        ...prev,
        name: '',
        description: '',
        status: 'pending',
        template_id: '',
      }));
    } else {
      const template = availableTemplates.find(t => t.id === id);
      if (template) {
        setSelectedTemplate(template);
      }
    }

    // Reset structured data
    setAcademicData([{ sNo: 1, courseName: '', lectureCompleted: 0, yearSem: '', noStudents: 0, noDistinctions: 0, noFirstClasses: 0, noPassed: 0 }]);
    setFeedbackData([{ sNo: 1, courseName: '', yearSem: '', noStudents: 0, feedbackPercentage: 0 }]);
    setResearchData([{ title: '', journalName: '', issnIsbn: '', indexing: 'SCI', impactFactor: '', coAuthors: 0, isMainAuthor: true }]);
    setTeachingData([{ activityDescription: '', qualityRating: 'Outstanding' }]);
    setFacultyScore(0);
    setResearchScore(0);
    setTeachingScore(0);
    setResearchProjectData([{ category: 'Project', title: '', details: '', periodOrYear: '', amountOrCount: '', status: 'Ongoing', policyOrPatent: false }]);
    setResearchProjectScore(0);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setUploadedFiles(prev => [...prev, ...files]);

      const newMetadata = { ...documentMetadata };
      files.forEach((file, index) => {
        const fileIndex = uploadedFiles.length + index;
        newMetadata[fileIndex] = {
          title: file.name,
          document_type: 'attachment',
          description: '',
          is_required: false
        };
      });
      setDocumentMetadata(newMetadata);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setDocumentMetadata(prev => {
      const newMeta: Record<string, any> = {};
      Object.keys(prev).forEach(key => {
        const k = parseInt(key);
        if (k < index) newMeta[k] = prev[k];
        if (k > index) newMeta[k - 1] = prev[k];
      });
      return newMeta;
    });
  };

  const updateDocumentMetadata = (index: number, field: string, value: any) => {
    setDocumentMetadata(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  };

  const getFileIcon = (mimeType: string, fileName: string) => {
    const type = mimeType || '';
    const name = fileName || '';
    if (type.includes('pdf') || name.endsWith('.pdf')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#fee2e2" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M10 12l4 4" />
          <path d="M14 12l-4 4" />
          <line x1="12" y1="12" x2="12" y2="12" />
        </svg>
      );
    }
    if (type.includes('word') || type.includes('document') || name.match(/\.(doc|docx)$/i)) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#dbeafe" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    }
    if (type.includes('excel') || type.includes('sheet') || name.match(/\.(xls|xlsx|csv)$/i)) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#d1fae5" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M8 13h8" />
          <path d="M8 17h8" />
          <path d="M10 9v8" />
        </svg>
      );
    }
    if (type.includes('image') || name.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" fill="#ede9fe" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      );
    }
    if (type.includes('presentation') || type.includes('powerpoint') || name.match(/\.(ppt|pptx)$/i)) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#fef3c7" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M10 12h4" />
          <path d="M12 12v6" />
        </svg>
      );
    }
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#f3f4f6" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    );
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      setUploadedFiles(prev => [...prev, ...files]);
      e.dataTransfer.clearData();
    }
  };

  // Structured Data Handlers
  const addAcademicRow = () => {
    setAcademicData(prev => [
      ...prev,
      { sNo: prev.length + 1, courseName: '', lectureCompleted: 0, yearSem: '', noStudents: 0, noDistinctions: 0, noFirstClasses: 0, noPassed: 0 }
    ]);
  };

  const removeAcademicRow = (index: number) => {
    if (academicData.length > 1) {
      setAcademicData(prev => prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, sNo: i + 1 })));
    }
  };

  const updateAcademicRow = (index: number, field: keyof AcademicRow, value: any) => {
    setAcademicData(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addFeedbackRow = () => {
    setFeedbackData(prev => [
      ...prev,
      { sNo: prev.length + 1, courseName: '', yearSem: '', noStudents: 0, feedbackPercentage: 0 }
    ]);
  };

  const removeFeedbackRow = (index: number) => {
    if (feedbackData.length > 1) {
      setFeedbackData(prev => prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, sNo: i + 1 })));
    }
  };

  const updateFeedbackRow = (index: number, field: keyof FeedbackRow, value: any) => {
    setFeedbackData(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addResearchRow = () => {
    setResearchData(prev => [
      ...prev,
      { title: '', journalName: '', issnIsbn: '', indexing: 'SCI', impactFactor: '', coAuthors: 0, isMainAuthor: true }
    ]);
  };

  const removeResearchRow = (index: number) => {
    if (researchData.length > 1) {
      setResearchData(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateResearchRow = (index: number, field: keyof ResearchPaperRow, value: any) => {
    setResearchData(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addTeachingRow = () => {
    setTeachingData(prev => [
      ...prev,
      { activityDescription: '', qualityRating: 'Outstanding' }
    ]);
  };

  const removeTeachingRow = (index: number) => {
    if (teachingData.length > 1) {
      setTeachingData(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateTeachingRow = (index: number, field: keyof TeachingActivityRow, value: any) => {
    setTeachingData(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addProfessionalRow = () => {
    setProfessionalData(prev => [
      ...prev,
      { activityType: 'Other', description: '', roleOrDuration: 'Leading/2+ Weeks/International' }
    ]);
  };

  const removeProfessionalRow = (index: number) => {
    if (professionalData.length > 1) {
      setProfessionalData(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateProfessionalRow = (index: number, field: keyof ProfessionalDevelopmentRow, value: any) => {
    setProfessionalData(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const calculateProfessionalScore = () => {
    let totalScore = 0;
    professionalData.forEach(row => {
      let score = 0;
      // Leading/High Duration = 5 (simplified standard) or 10 for Intl Conf.
      // Member/Medium = 2 (or 5)
      // Low = 1

      // Using a simplified model based on the "Role/Duration" dropdown to capture the high/med/low logic
      if (row.roleOrDuration === 'Leading/2+ Weeks/International') {
        score = 5; // Default high
        if (row.activityType === 'Conference') score = 10; // Exception for Intl Conference
      } else if (row.roleOrDuration === 'Member/1 Week/National') {
        score = 2; // Default med
        if (row.activityType === 'Conference') score = 5; // Exception for National Conference
        if (row.activityType === 'FDP') score = 2; // 1 Week FDP
      } else if (row.roleOrDuration === 'Participant/Low Duration') {
        score = 1;
      }
      totalScore += score;
    });
    setProfessionalScore(totalScore);
  };

  const addResearchProjectRow = () => {
    setResearchProjectData(prev => [
      ...prev,
      { category: 'Project', title: '', details: '', periodOrYear: '', amountOrCount: '', status: 'Ongoing', policyOrPatent: false }
    ]);
  };

  const removeResearchProjectRow = (index: number) => {
    if (researchProjectData.length > 1) {
      setResearchProjectData(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateResearchProjectRow = (index: number, field: keyof ResearchProjectRow, value: any) => {
    setResearchProjectData(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const calculateResearchProjectScore = () => {
    let totalScore = 0;
    researchProjectData.forEach(row => {
      let score = 0;
      switch (row.category) {
        case 'Book':
          // Book International: 25, National: 15, Local: 10
          // Simplifying for now to 15 default
          score = 15;
          break;
        case 'Chapter':
          score = 10;
          break;
        case 'Project':
          // Completed: 10, Policy/Patent: +?
          score = 10;
          if (row.policyOrPatent) score += 5;
          break;
        case 'Consultancy':
          score = 10;
          break;
        case 'Guidance':
          // PhD Completed: 10, Submitted: 7, MTech: 3
          if (row.status === 'Completed') score = 10;
          else if (row.status === 'Submitted') score = 7;
          else score = 3; // Ongoing/MTech
          break;
        default:
          score = 0;
      }
      totalScore += score;
    });
    setResearchProjectScore(totalScore);
  };

  useEffect(() => {
    if (isProfessionalDevelopment) {
      calculateProfessionalScore();
    }
  }, [professionalData, isProfessionalDevelopment]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedTemplateId === 'others') {
      if (!formData.name.trim()) {
        notificationService.error('Please enter a task name');
        return;
      }
      if (userGroups.length > 1 && !selectedGroupId) {
        notificationService.error('Please select a group for this request');
        return;
      }
      if (!formData.description.trim()) {
        notificationService.error('Please enter a description');
        return;
      }
      if (!user?.id) {
        notificationService.error('User information not available');
        return;
      }

      setIsSubmitting(true);
      try {
        // Construct TaskProposal payload
        const proposalPayload = {
          title: formData.name.trim(),
          description: formData.description || '',
          group: selectedGroupId || null,
          priority: 'medium',
          status: 'pending_approval', // Submit for approval
          requested_task_data: {
            // Store any additional context if needed
            files_metadata: documentMetadata, // Basic metadata if files are uploaded (actual files not supported in this API call yet)
            academic_year: academicYear
          }
        };

        const response = await apiService.createProposal(proposalPayload);

        // Check for success status (wrapped) or direct ID (raw object)
        const isSuccess = (response as any).status === 'success' || (response as any).id;

        if (isSuccess) {
          notificationService.success('Custom proposal submitted successfully');
          onSuccess();
          onClose();
        } else {
          throw new Error(response.message || 'Failed to submit proposal');
        }

      } catch (error: any) {
        console.error('Error submitting proposal:', error);
        notificationService.error(error.message || 'Failed to submit proposal');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!selectedTemplateId) {
      notificationService.error('Please select a template');
      return;
    }

    if (userGroups.length > 1 && !selectedGroupId) {
      notificationService.error('Please select a group for this initiation');
      return;
    }

    // Validation for specialized templates
    if ((isAcademicPerformance || isFeedback || isResearchPublication || isTeachingActivity || isProfessionalDevelopment || isResearchProject) && !academicYear) {
      notificationService.error('Please select an Academic Year');
      return;
    }

    if (isAcademicPerformance) {
      const isValid = academicData.every(row =>
        row.courseName &&
        row.courseName.trim() !== '' &&
        row.yearSem &&
        row.yearSem.trim() !== '' &&
        row.noStudents > 0
      );
      if (!isValid) {
        notificationService.error('Please fill in required fields in Academic Performance table.');
        return;
      }
    }

    if (isFeedback) {
      const isValid = feedbackData.every(row => row.courseName && row.yearSem && row.noStudents > 0);
      if (!isValid) {
        notificationService.error('Please fill in required fields in Feedback table.');
        return;
      }
    }

    if (isResearchPublication) {
      const isValid = researchData.every(row => row.title && row.journalName && row.indexing);
      if (!isValid) {
        notificationService.error('Please fill in Paper Title and Journal Name.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('template_id', selectedTemplateId);
      formDataObj.append('name', formData.name.trim() || (selectedTemplate?.name || ''));
      formDataObj.append('description', formData.description || (selectedTemplate?.description || ''));
      formDataObj.append('status', formData.status);

      if (selectedGroupId) {
        formDataObj.append('group', selectedGroupId);
      }

      if (academicYear) {
        formDataObj.append('academic_year', academicYear);
      }

      // Bundle specialized metadata
      const metadata: Record<string, any> = {
        is_instance: true,
        academic_year: academicYear
      };

      if (isAcademicPerformance) {
        metadata.type = 'academic_performance';
        metadata.academic_data = academicData;
        metadata.faculty_score = facultyScore;
      } else if (isFeedback) {
        metadata.type = 'feedback';
        metadata.feedback_data = feedbackData;
      } else if (isResearchPublication) {
        metadata.type = 'research_publication';
        metadata.research_data = researchData;
        metadata.research_score = researchScore;
      } else if (isTeachingActivity) {
        metadata.type = 'teaching_activity';
        metadata.teaching_data = teachingData;
        metadata.teaching_score = teachingScore;
      } else if (isProfessionalDevelopment) {
        metadata.type = 'professional_development';
        metadata.professional_data = professionalData;
        metadata.professional_score = professionalScore;
      } else if (isResearchProject) {
        metadata.type = 'research_project';
        metadata.research_project_data = researchProjectData;
        metadata.research_project_score = researchProjectScore;
      }

      formDataObj.append('metadata', JSON.stringify(metadata));

      // Append files
      uploadedFiles.forEach((file) => formDataObj.append('files', file));

      const response = await apiService.initiateTaskInstance(formDataObj);

      if (response.status === 'success') {
        notificationService.success('Task instance initiated successfully');
        onSuccess();
        onClose();
      } else {
        notificationService.error(response.message || 'Failed to initiate task');
      }
    } catch (error: any) {
      console.error('Error initiating task:', error);
      notificationService.error(error.response?.data?.message || 'Error occurred while initiating task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const templateOptions = [
    ...availableTemplates.map((template) => ({
      value: template.id,
      label: template.name,
      subtitle: [
        template.group?.name,
        template.category?.name,
        template.priority ? `Priority: ${template.priority}` : null,
        template.estimated_hours ? `~${template.estimated_hours} hours` : null
      ].filter(Boolean).join(' • '),
    })),
    ...(enableCustomProposal ? [{
      value: 'others',
      label: 'Others (Custom Proposal)',
      subtitle: 'Create a new task without a template'
    }] : [])
  ];

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={selectedTemplateId === 'others' ? "New Request" : "Create Task Initiation from Template"}
    >
      <form onSubmit={handleSubmit} className={sidePanelStyles.form} style={{ paddingTop: '0' }}>
        {/* Step 1: Template Selection */}
        {!selectedTemplate && selectedTemplateId !== 'others' && (
          <div>
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Select a Template <span className="required">*</span>
              </label>
              {isLoadingTemplates ? (
                <SkeletonLoader variant="list" count={3} />
              ) : availableTemplates.length === 0 && !enableCustomProposal ? (
                <EmptyState
                  image="/images/notasks.png"
                  title="No Templates Available"
                  description="There are no task templates available for you to create initiations from."
                  action={{ label: "Refresh Templates", onClick: onLoadTemplates }}
                />
              ) : (
                <CustomDropdown
                  options={templateOptions}
                  value={selectedTemplateId}
                  onChange={handleTemplateSelect}
                  placeholder="Search and select a template..."
                  searchable={true}
                  size="full"
                  variant="outlined"
                />
              )}
            </div>
            {availableTemplates.length === 0 && enableCustomProposal && !selectedTemplateId && (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <p style={{ color: 'var(--gray)', marginBottom: '0.5rem' }}>No templates? Create a custom request.</p>
                <button type="button" className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`} onClick={() => handleTemplateSelect('others')}>
                  Create Custom Request
                </button>
              </div>
            )}
            <div className={sidePanelStyles.formActions}>
              <button type="button" className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`} onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}

        {/* Step 2: Initiation Customization */}
        {((selectedTemplate && templateDetails) || selectedTemplateId === 'others') && !loadingDetails && (
          <div>
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={() => handleTemplateSelect('')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--primary)',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 500
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5z" />
                </svg>
                Back to Templates
              </button>
            </div>

            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Academic Year <span className="required">*</span>
              </label>
              <CustomDropdown
                options={academicYearOptions}
                value={academicYear}
                onChange={(val) => setAcademicYear(val as string)}
                placeholder="Select Academic Year"
                size="full"
                variant="outlined"
              />
            </div>

            {/* Group Selector for Multi-Group Users (Only for Global templates or Custom proposals) */}
            {userGroups.length > 1 && !selectedTemplate?.group && (
              <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>
                  Select Group <span className="required">*</span>
                  <small style={{ display: 'block', color: 'var(--gray)', fontWeight: 'normal', marginTop: '0.25rem' }}>
                    Select which group this task belongs to for approval.
                  </small>
                </label>
                <CustomDropdown
                  options={userGroups.map(g => ({ value: g.id, label: g.name }))}
                  value={selectedGroupId}
                  onChange={(val) => setSelectedGroupId(val as string)}
                  placeholder="Select a group..."
                  size="full"
                  variant="outlined"
                />
              </div>
            )}

            {/* Read-only Group display if template already has a group */}
            {selectedTemplate?.group && (
              <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>Group</label>
                <div style={{ padding: '0.75rem', backgroundColor: 'var(--gray-lighter)', borderRadius: '8px', border: '1px solid var(--border)', color: 'var(--dark)', fontWeight: '500' }}>
                  {selectedTemplate.group.name}
                </div>
              </div>
            )}
            {/* Template Info / Group Selector Omitted for Brevity - Keeping Core UI logic */}
            {selectedTemplate && (
              <div className={sidePanelStyles.formGroup}>
                <div style={{ padding: '1rem', backgroundColor: 'var(--gray-lighter)', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="var(--primary)" viewBox="0 0 16 16"><path fillRule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z" /></svg>
                    <strong style={{ color: 'var(--primary)', fontWeight: '700' }}>Creating initiation from template:</strong>
                  </div>
                  <p style={{ margin: 0, color: 'var(--dark)', fontWeight: '500' }}>{selectedTemplate.name}</p>
                </div>
              </div>
            )}

            {/* Special Forms for Academic/Feedback */}
            {isAcademicPerformance && (
              <div className={sidePanelStyles.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <label className={sidePanelStyles.formLabel} style={{ marginBottom: 0 }}>Academic Performance Details</label>
                  <div style={{ fontWeight: 'bold', fontSize: '1rem', color: 'var(--primary)' }}>
                    Score: {facultyScore}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {academicData.map((row, index) => (
                    <div key={index} style={{
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '1rem',
                      backgroundColor: '#fff',
                      position: 'relative'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Subject #{row.sNo}</span>
                        {academicData.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAcademicRow(index)}
                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>
                          Subject Name <span className="required" style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                          type="text"
                          className={sidePanelStyles.formControl}
                          value={row.courseName}
                          onChange={(e) => updateAcademicRow(index, 'courseName', e.target.value)}
                          placeholder="Enter subject name"
                          required
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>
                            Year/Sem <span className="required" style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            type="text"
                            className={sidePanelStyles.formControl}
                            value={row.yearSem}
                            onChange={(e) => updateAcademicRow(index, 'yearSem', e.target.value)}
                            placeholder="e.g. III-II"
                            required
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>
                            No. Students <span className="required" style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            type="number"
                            className={sidePanelStyles.formControl}
                            value={row.noStudents}
                            onChange={(e) => updateAcademicRow(index, 'noStudents', parseInt(e.target.value) || 0)}
                            min={0}
                            required
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.8rem' }}>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            Lecture % <span className="required" style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            type="number"
                            className={sidePanelStyles.formControl}
                            value={row.lectureCompleted}
                            onChange={(e) => updateAcademicRow(index, 'lectureCompleted', parseFloat(e.target.value) || 0)}
                            min={0}
                            max={100}
                            required
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            No. Passed <span className="required" style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            type="number"
                            className={sidePanelStyles.formControl}
                            value={row.noPassed}
                            onChange={(e) => updateAcademicRow(index, 'noPassed', parseInt(e.target.value) || 0)}
                            min={0}
                            required
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            No. 1st Class <span className="required" style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            type="number"
                            className={sidePanelStyles.formControl}
                            value={row.noFirstClasses}
                            onChange={(e) => updateAcademicRow(index, 'noFirstClasses', parseInt(e.target.value) || 0)}
                            min={0}
                            required
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            No. Distinction <span className="required" style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            type="number"
                            className={sidePanelStyles.formControl}
                            value={row.noDistinctions}
                            onChange={(e) => updateAcademicRow(index, 'noDistinctions', parseInt(e.target.value) || 0)}
                            min={0}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addAcademicRow}
                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    + Add Another Subject
                  </button>
                </div>
              </div>
            )}

            {isFeedback && (
              <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel} style={{ marginBottom: '1rem' }}>Feedback Details</label>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {feedbackData.map((row, index) => (
                    <div key={index} style={{
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '1rem',
                      backgroundColor: '#fff',
                      position: 'relative'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Course #{row.sNo}</span>
                        {feedbackData.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFeedbackRow(index)}
                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Course Handled</label>
                        <input
                          type="text"
                          className={sidePanelStyles.formControl}
                          value={row.courseName}
                          onChange={(e) => updateFeedbackRow(index, 'courseName', e.target.value)}
                          placeholder="Enter course name"
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Year/Sem</label>
                          <input
                            type="text"
                            className={sidePanelStyles.formControl}
                            value={row.yearSem}
                            onChange={(e) => updateFeedbackRow(index, 'yearSem', e.target.value)}
                            placeholder="e.g. III-II"
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>No. Students</label>
                          <input
                            type="number"
                            className={sidePanelStyles.formControl}
                            value={row.noStudents}
                            onChange={(e) => updateFeedbackRow(index, 'noStudents', parseInt(e.target.value))}
                            min={0}
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Feedback %</label>
                          <input
                            type="number"
                            className={sidePanelStyles.formControl}
                            value={row.feedbackPercentage}
                            onChange={(e) => updateFeedbackRow(index, 'feedbackPercentage', parseFloat(e.target.value))}
                            min={0}
                            max={100}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addFeedbackRow}
                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    + Add Another Course
                  </button>
                </div>
              </div>
            )}

            {isResearchPublication && (
              <div className={sidePanelStyles.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <label className={sidePanelStyles.formLabel} style={{ marginBottom: 0 }}>Research Papers (Part C)</label>
                  <div style={{ fontWeight: 'bold', fontSize: '1rem', color: 'var(--primary)' }}>
                    Score: {researchScore}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {researchData.map((row, index) => (
                    <div key={index} style={{
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '1rem',
                      backgroundColor: '#fff',
                      position: 'relative'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Paper #{index + 1}</span>
                        {researchData.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeResearchRow(index)}
                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Title of Paper</label>
                        <input
                          type="text"
                          className={sidePanelStyles.formControl}
                          value={row.title}
                          onChange={(e) => updateResearchRow(index, 'title', e.target.value)}
                          placeholder="Enter paper title"
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Journal Name</label>
                          <input
                            type="text"
                            className={sidePanelStyles.formControl}
                            value={row.journalName}
                            onChange={(e) => updateResearchRow(index, 'journalName', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>ISSN / ISBN No.</label>
                          <input
                            type="text"
                            className={sidePanelStyles.formControl}
                            value={row.issnIsbn}
                            onChange={(e) => updateResearchRow(index, 'issnIsbn', e.target.value)}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Indexing</label>
                          <CustomDropdown
                            options={[
                              { value: 'SCI', label: 'SCI/SCI-E' },
                              { value: 'Scopus', label: 'Scopus' },
                              { value: 'Web of Science', label: 'Web of Science' },
                              { value: 'UGC', label: 'UGC' },
                              { value: 'Other', label: 'Other' }
                            ]}
                            value={row.indexing}
                            onChange={(val) => updateResearchRow(index, 'indexing', val)}
                            placeholder="Select Indexing"
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Impact Factor</label>
                          <input
                            type="text"
                            className={sidePanelStyles.formControl}
                            value={row.impactFactor}
                            onChange={(e) => updateResearchRow(index, 'impactFactor', e.target.value)}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '1.5rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem' }}>
                            <input
                              type="checkbox"
                              checked={row.isMainAuthor}
                              onChange={(e) => updateResearchRow(index, 'isMainAuthor', e.target.checked)}
                              style={{ width: '16px', height: '16px', marginRight: '0.5rem' }}
                            />
                            Main/First Author?
                          </label>
                        </div>
                      </div>

                      {!row.isMainAuthor && (
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Number of Co-Authors (excluding you)</label>
                          <input
                            type="number"
                            className={sidePanelStyles.formControl}
                            value={row.coAuthors}
                            onChange={(e) => updateResearchRow(index, 'coAuthors', parseInt(e.target.value) || 0)}
                            min={0}
                          />
                        </div>
                      )}

                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addResearchRow}
                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    + Add Another Paper
                  </button>
                </div>
              </div>
            )}

            {isTeachingActivity && (
              <div className={sidePanelStyles.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <label className={sidePanelStyles.formLabel} style={{ marginBottom: 0 }}>Teaching & Learning Activities (Part A)</label>
                  <div style={{ fontWeight: 'bold', fontSize: '1rem', color: 'var(--primary)' }}>
                    Score: {teachingScore}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {teachingData.map((row, index) => (
                    <div key={index} style={{
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '1rem',
                      backgroundColor: '#fff',
                      position: 'relative'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Activity #{index + 1}</span>
                        {teachingData.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTeachingRow(index)}
                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Activity Description</label>
                        <textarea
                          className={sidePanelStyles.formControl}
                          value={row.activityDescription}
                          onChange={(e) => updateTeachingRow(index, 'activityDescription', e.target.value)}
                          placeholder="Describe the teaching methodology or activity..."
                          rows={2}
                        />
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Quality Rating</label>
                        <CustomDropdown
                          options={[
                            { value: 'Outstanding', label: 'Outstanding (5 pts)' },
                            { value: 'Average', label: 'Average (3 pts)' },
                            { value: 'Modest', label: 'Modest (1 pt)' }
                          ]}
                          value={row.qualityRating}
                          onChange={(val) => updateTeachingRow(index, 'qualityRating', val)}
                          placeholder="Select Rating"
                          size="full"
                          variant="outlined"
                        />
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addTeachingRow}
                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    + Add Another Activity
                  </button>
                </div>
              </div>
            )}

            {isResearchProject && (
              <div className={sidePanelStyles.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <label className={sidePanelStyles.formLabel} style={{ marginBottom: 0 }}>Research Projects & Guidance</label>
                  <div style={{ fontWeight: 'bold', fontSize: '1rem', color: 'var(--primary)' }}>
                    Score: {researchProjectScore}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {researchProjectData.map((row, index) => (
                    <div key={index} style={{
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '1rem',
                      backgroundColor: '#fff',
                      position: 'relative'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Entry #{index + 1}</span>
                        {researchProjectData.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeResearchProjectRow(index)}
                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Category</label>
                        <CustomDropdown
                          options={[
                            { value: 'Book', label: 'Book' },
                            { value: 'Chapter', label: 'Book Chapter' },
                            { value: 'Project', label: 'Funded Project' },
                            { value: 'Consultancy', label: 'Consultancy' },
                            { value: 'Guidance', label: 'Research Guidance' }
                          ]}
                          value={row.category}
                          onChange={(val) => updateResearchProjectRow(index, 'category', val)}
                          placeholder="Select Category"
                          size="full"
                          variant="outlined"
                        />
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Title / Topic</label>
                        <input
                          type="text"
                          className={sidePanelStyles.formControl}
                          value={row.title}
                          onChange={(e) => updateResearchProjectRow(index, 'title', e.target.value)}
                          placeholder="Title"
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Details (Agency/Publisher)</label>
                          <input
                            type="text"
                            className={sidePanelStyles.formControl}
                            value={row.details}
                            onChange={(e) => updateResearchProjectRow(index, 'details', e.target.value)}
                            placeholder="Details"
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Period / Year</label>
                          <input
                            type="text"
                            className={sidePanelStyles.formControl}
                            value={row.periodOrYear}
                            onChange={(e) => updateResearchProjectRow(index, 'periodOrYear', e.target.value)}
                            placeholder="Year/Period"
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Metric (Amount/Count)</label>
                          <input
                            type="text"
                            className={sidePanelStyles.formControl}
                            value={row.amountOrCount}
                            onChange={(e) => updateResearchProjectRow(index, 'amountOrCount', e.target.value)}
                            placeholder="Amount/Count"
                          />
                        </div>
                        <div>
                          <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Status</label>
                          <CustomDropdown
                            options={[
                              { value: 'Ongoing', label: 'Ongoing' },
                              { value: 'Completed', label: 'Completed' },
                              { value: 'Submitted', label: 'Submitted' },
                              { value: 'Published', label: 'Published' }
                            ]}
                            value={row.status}
                            onChange={(val) => updateResearchProjectRow(index, 'status', val)}
                            placeholder="Status"
                            size="full"
                            variant="outlined"
                          />
                        </div>
                      </div>

                      {row.category === 'Project' && (
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem' }}>
                            <input
                              type="checkbox"
                              checked={row.policyOrPatent}
                              onChange={(e) => updateResearchProjectRow(index, 'policyOrPatent', e.target.checked)}
                              style={{ width: '16px', height: '16px', marginRight: '0.5rem' }}
                            />
                            Resulted in Policy/Patent?
                          </label>
                        </div>
                      )}

                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addResearchProjectRow}
                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    + Add Another Entry
                  </button>
                </div>
              </div>
            )}

            {isProfessionalDevelopment && (
              <div className={sidePanelStyles.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <label className={sidePanelStyles.formLabel} style={{ marginBottom: 0 }}>Professional Development (Part B)</label>
                  <div style={{ fontWeight: 'bold', fontSize: '1rem', color: 'var(--primary)' }}>
                    Score: {professionalScore}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {professionalData.map((row, index) => (
                    <div key={index} style={{
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '1rem',
                      backgroundColor: '#fff',
                      position: 'relative'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Activity #{index + 1}</span>
                        {professionalData.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeProfessionalRow(index)}
                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Activity Type</label>
                        <CustomDropdown
                          options={[
                            { value: 'Extension Activity', label: 'Extension / Co-curricular' },
                            { value: 'Admin Role', label: 'Administrative Role' },
                            { value: 'FDP', label: 'FDP / Workshop / Training' },
                            { value: 'Conference', label: 'Conference Organization' },
                            { value: 'Other', label: 'Other' }
                          ]}
                          value={row.activityType}
                          onChange={(val) => updateProfessionalRow(index, 'activityType', val)}
                          placeholder="Select Type"
                          size="full"
                          variant="outlined"
                        />
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Description / Title</label>
                        <input
                          type="text"
                          className={sidePanelStyles.formControl}
                          value={row.description}
                          onChange={(e) => updateProfessionalRow(index, 'description', e.target.value)}
                          placeholder="E.g., Convenor for Tech Fest, Warden, Attended AI Workshop..."
                        />
                      </div>

                      <div className={sidePanelStyles.formGroup}>
                        <label className={sidePanelStyles.formLabel} style={{ fontSize: '0.85rem' }}>Level / Role / Duration</label>
                        <CustomDropdown
                          options={[
                            { value: 'Leading/2+ Weeks/International', label: 'Leading Role / 2+ Weeks / International' },
                            { value: 'Member/1 Week/National', label: 'Member / 1 Week / National' },
                            { value: 'Participant/Low Duration', label: 'Participant / <1 Week / Local' }
                          ]}
                          value={row.roleOrDuration}
                          onChange={(val) => updateProfessionalRow(index, 'roleOrDuration', val)}
                          placeholder="Select Category"
                          size="full"
                          variant="outlined"
                        />
                      </div>

                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addProfessionalRow}
                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    + Add Another Activity
                  </button>
                </div>
              </div>
            )}


            {/* Group/Department Selection for Custom Proposals */}
            {selectedTemplateId === 'others' && userGroups.length > 1 && (
              <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>
                  Select Department/Group <span className="required" style={{ color: 'red' }}>*</span>
                </label>
                <select
                  className={sidePanelStyles.formControl}
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  disabled={!!contextGroupId} // Disable if already in context
                >
                  <option value="">Select Group</option>
                  {userGroups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                {selectedTemplateId === 'others' ? 'Task Name' : 'Task Initiation Name'} <span className="required">*</span>
              </label>
              <input
                type="text"
                className={sidePanelStyles.formControl}
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={selectedTemplateId === 'others' ? "Enter task name" : "Enter task initiation name"}
                required
                disabled={selectedTemplateId !== 'others'}
              />
            </div>

            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Description {(selectedTemplateId === 'others' || (selectedTemplate as any)?.requires_documents || templateDetails?.requires_documents) && <span className="required" style={{ color: 'red' }}>*</span>}
              </label>
              <textarea
                className={sidePanelStyles.formControl}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description..."
                rows={3}
                disabled={selectedTemplateId !== 'others'}
              />
            </div>

            {/* Document Upload Section */}
            <div className={sidePanelStyles.formGroup}>
              <label className={sidePanelStyles.formLabel}>
                Attach Documents {(selectedTemplateId === 'others' || (selectedTemplate as any)?.requires_documents || templateDetails?.requires_documents) && <span className="required" style={{ color: 'red' }}>*</span>}
              </label>
              <div
                className={styles.fileUploadArea}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="documentUpload"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.ppt,.pptx"
                  onChange={handleFileUpload}
                  className={styles.fileInput}
                />
                <label htmlFor="documentUpload" className={styles.fileUploadLabel}>
                  <div className={styles.fileUploadIcon}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className={styles.fileUploadText}>
                    {isValidatingFiles ? (
                      <>
                        <strong>Validating files for security...</strong>
                        <span>Please wait while we scan your files</span>
                      </>
                    ) : (
                      <>
                        <strong>Drag & drop your files here</strong>
                        <span>
                          or <span className={styles.browseLink}>browse</span> to select files
                        </span>
                      </>
                    )}
                    <div className={styles.acceptedFileTypes}>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('application/pdf', 'document.pdf')}</span>
                        <span>.pdf</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('application/msword', 'document.doc')}</span>
                        <span>.doc</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'document.docx')}</span>
                        <span>.docx</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        {getFileIcon('text/plain', 'document.txt')}
                        <span>.txt</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('image/jpeg', 'image.jpg')}</span>
                        <span>.jpg</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('image/png', 'image.png')}</span>
                        <span>.png</span>
                      </div>
                      <div className={styles.fileTypeItem}>
                        <span>{getFileIcon('application/vnd.ms-excel', 'spreadsheet.xlsx')}</span>
                        <span>.xlsx</span>
                      </div>
                    </div>
                    <small>
                      Max 50MB each • Security scanning enabled • Executable files blocked
                    </small>
                  </div>
                </label>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className={styles.uploadedFiles}>
                  <h4 className={styles.uploadedFilesTitle}>Uploaded Files ({uploadedFiles.length})</h4>
                  {uploadedFiles.map((file, index) => {
                    const metadata = documentMetadata[index];
                    return (
                      <div key={index} className={styles.uploadedFileItem}>
                        <div className={styles.fileInfo}>
                          <div className={styles.fileIcon}>
                            {getFileIcon(file.type, file.name)}
                          </div>
                          <div className={styles.fileDetails}>
                            <div className={styles.fileName}>{file.name}</div>
                            <div className={styles.fileSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className={styles.removeFileBtn}
                            title="Remove file"
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>

                        {/* File Metadata - Simplified: Only Title */}
                        <div className={styles.fileMetadata}>
                          <div className={sidePanelStyles.formGroup}>
                            <label className={sidePanelStyles.formLabel}>Title</label>
                            <input
                              type="text"
                              className={sidePanelStyles.formControl}
                              value={metadata?.title || ''}
                              onChange={(e) => updateDocumentMetadata(index, 'title', e.target.value)}
                              placeholder="Document title"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={sidePanelStyles.formActions}>
              <button
                type="button"
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Initiation'}
              </button>
            </div>
          </div>
        )}
      </form>
    </SidePanel>
  );
};

export default TaskInitiationForm;